import { SERVICE_CATEGORY_TOKEN } from './catalog.js';
import { isValidTimeZone, localCalendarDate, parseCalendarDate, validityEndFromIssue } from './validity.js';
/** The decision version stamped on an in-person review line. */
export const KYC_IN_PERSON_REVIEW_VERSION = 'kyc-in-person-review-v1';
/** The decision version stamped on an operator vehicle inspection. */
export const VEHICLE_INSPECTION_VERSION = 'vehicle-inspection-v1';
/** The only reason an approval carries: the operator saw the original and it matches. */
export const KYC_REVIEW_APPROVE_REASONS = ['IN_PERSON_VERIFIED'];
/** Why a line is rejected. `OTHER` requires a note. */
export const KYC_REVIEW_REJECT_REASONS = [
    'DOCUMENT_ILLEGIBLE',
    'DOCUMENT_EXPIRED_AT_REVIEW',
    'NUMBER_MISMATCH',
    'HOLDER_MISMATCH',
    'NOT_ORIGINAL',
    'MISSING_REQUIRED_ENDORSEMENT',
    'VEHICLE_MISMATCH',
    'OTHER',
];
/** The reason the expiry sweep stamps when a document's validity elapses. */
export const KYC_VALIDITY_ELAPSED = 'VALIDITY_ELAPSED';
/**
 * The physical vehicle classes an inspection verifies (mirrors `@vello/service-categories` `VEHICLE_CLASSES`). The driver
 * types the vehicle's type as free text in the app; dispatch uses the class the operator verified.
 */
export const INSPECTION_VEHICLE_CLASSES = ['bike', 'moto', 'car', 'van', 'utility', 'truck'];
export const VEHICLE_INSPECTION_PASS_REASONS = ['INSPECTION_PASSED'];
export const VEHICLE_INSPECTION_FAIL_REASONS = ['VEHICLE_CONDITION_FAILED', 'PLATE_MISMATCH', 'CATEGORY_NOT_MET', 'VEHICLE_DOCUMENTS_MISSING', 'OTHER'];
export const REVIEW_NOTE_MAX = 500;
export const DOCUMENT_NUMBER_MAX = 64;
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
const isUtcInstant = (v) => typeof v === 'string' && UTC_INSTANT.test(v) && !Number.isNaN(Date.parse(v));
const str = (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined);
/**
 * Validate one review line against its catalog type at `at`. Returns the normalized line or the first refusal. `timeZone`
 * is the market's IANA zone: an issue date is a day on its wall clock, and a validity computed from it ends at the end of
 * that day there.
 */
export function validateInPersonReview(type, raw, at, opts = {}) {
    if (type === undefined)
        return { ok: false, error: 'TYPE_NOT_IN_CATALOG' };
    if (!type.active)
        return { ok: false, error: 'TYPE_RETIRED' };
    const result = raw.result;
    if (result !== 'APPROVED' && result !== 'REJECTED')
        return { ok: false, error: 'RESULT_INVALID' };
    const reasonCode = str(raw.reasonCode);
    const allowed = result === 'APPROVED' ? KYC_REVIEW_APPROVE_REASONS : KYC_REVIEW_REJECT_REASONS;
    if (reasonCode === undefined || !allowed.includes(reasonCode))
        return { ok: false, error: 'REASON_INVALID' };
    const note = str(raw.note);
    if (note !== undefined && note.length > REVIEW_NOTE_MAX)
        return { ok: false, error: 'NOTE_TOO_LONG' };
    if (reasonCode === 'OTHER' && note === undefined)
        return { ok: false, error: 'NOTE_REQUIRED' };
    const number = str(raw.number);
    if (number !== undefined && (number.length > DOCUMENT_NUMBER_MAX || number.replace(/[\s.\-/]/g, '').length < 2))
        return { ok: false, error: 'NUMBER_INVALID' };
    if (result === 'APPROVED' && type.rules.number && number === undefined)
        return { ok: false, error: 'NUMBER_REQUIRED' };
    const printedRaw = raw.expiresAt === undefined || raw.expiresAt === null || raw.expiresAt === '' ? undefined : raw.expiresAt;
    if (printedRaw !== undefined && !isUtcInstant(printedRaw))
        return { ok: false, error: 'EXPIRY_INVALID' };
    const printed = printedRaw;
    const issuedRaw = raw.issuedOn === undefined || raw.issuedOn === null || raw.issuedOn === '' ? undefined : raw.issuedOn;
    if (issuedRaw !== undefined && (typeof issuedRaw !== 'string' || parseCalendarDate(issuedRaw) === undefined))
        return { ok: false, error: 'ISSUE_DATE_INVALID' };
    const issuedOn = typeof issuedRaw === 'string' ? issuedRaw.trim() : undefined;
    // An issue date is a day on the market's wall clock (the UTC day when the zone is unknown): never after today there.
    if (issuedOn !== undefined && issuedOn > localCalendarDate(Date.parse(at), isValidTimeZone(opts.timeZone) ? opts.timeZone : 'UTC'))
        return { ok: false, error: 'ISSUE_DATE_IN_FUTURE' };
    // A printed expiry governs; with none, the type's period from the issue date (owner answer, PR 291 question 4).
    let expiresAt = printed;
    if (expiresAt === undefined && issuedOn !== undefined && type.validityFromIssue !== undefined) {
        expiresAt = validityEndFromIssue(issuedOn, type.validityFromIssue, opts.timeZone);
        if (expiresAt === undefined)
            return { ok: false, error: 'EXPIRY_INVALID' };
    }
    if (result === 'APPROVED' && type.rules.expiry && expiresAt === undefined)
        return { ok: false, error: 'EXPIRY_REQUIRED' };
    if (result === 'APPROVED' && expiresAt !== undefined && Date.parse(expiresAt) <= Date.parse(at))
        return { ok: false, error: 'EXPIRY_IN_PAST' };
    const vehicleId = str(raw.vehicleId);
    if (type.subject === 'VEHICLE' && vehicleId === undefined)
        return { ok: false, error: 'VEHICLE_REQUIRED' };
    if (type.subject === 'PERSON' && vehicleId !== undefined)
        return { ok: false, error: 'VEHICLE_NOT_ALLOWED' };
    return {
        ok: true,
        value: {
            documentType: type.code,
            result,
            reasonCode: reasonCode,
            ...(number !== undefined ? { number } : {}),
            ...(issuedOn !== undefined ? { issuedOn } : {}),
            ...(expiresAt !== undefined ? { expiresAt } : {}),
            ...(note !== undefined ? { note } : {}),
            ...(vehicleId !== undefined ? { vehicleId } : {}),
        },
    };
}
/** Upper case, no spaces, dots or dashes — the comparison form of a plate (the country adapter formats it for display). */
export function normalizePlate(plate) {
    return plate.toUpperCase().replace(/[\s.-]/g, '');
}
/** Validate an inspection of `registeredPlate` at `at`. A PASSED inspection must read the registered plate. */
export function validateVehicleInspection(raw, registeredPlate, at) {
    const result = raw.result;
    if (result !== 'PASSED' && result !== 'FAILED')
        return { ok: false, error: 'RESULT_INVALID' };
    const reasonCode = str(raw.reasonCode);
    const allowed = result === 'PASSED' ? VEHICLE_INSPECTION_PASS_REASONS : VEHICLE_INSPECTION_FAIL_REASONS;
    if (reasonCode === undefined || !allowed.includes(reasonCode))
        return { ok: false, error: 'REASON_INVALID' };
    const note = str(raw.note);
    if (note !== undefined && note.length > REVIEW_NOTE_MAX)
        return { ok: false, error: 'NOTE_TOO_LONG' };
    if (reasonCode === 'OTHER' && note === undefined)
        return { ok: false, error: 'NOTE_REQUIRED' };
    const categories = Array.isArray(raw.categories) ? raw.categories : raw.categories === undefined ? [] : undefined;
    if (categories === undefined || !categories.every((c) => typeof c === 'string' && SERVICE_CATEGORY_TOKEN.test(c)))
        return { ok: false, error: 'CATEGORY_INVALID' };
    const unique = [...new Set(categories)].sort();
    if (result === 'PASSED' && unique.length === 0)
        return { ok: false, error: 'CATEGORIES_REQUIRED' };
    const vehicleClass = str(raw.vehicleClass);
    if (vehicleClass !== undefined && !INSPECTION_VEHICLE_CLASSES.includes(vehicleClass))
        return { ok: false, error: 'VEHICLE_CLASS_INVALID' };
    if (result === 'PASSED' && vehicleClass === undefined)
        return { ok: false, error: 'VEHICLE_CLASS_REQUIRED' };
    const plate = str(raw.plate);
    if (plate === undefined)
        return { ok: false, error: 'PLATE_REQUIRED' };
    if (result === 'PASSED' && normalizePlate(plate) !== normalizePlate(registeredPlate))
        return { ok: false, error: 'PLATE_MISMATCH' };
    const validUntil = raw.validUntil === undefined || raw.validUntil === null || raw.validUntil === '' ? undefined : raw.validUntil;
    if (validUntil !== undefined && !isUtcInstant(validUntil))
        return { ok: false, error: 'VALID_UNTIL_INVALID' };
    if (validUntil !== undefined && Date.parse(validUntil) <= Date.parse(at))
        return { ok: false, error: 'VALID_UNTIL_IN_PAST' };
    return {
        ok: true,
        value: {
            result,
            categories: result === 'PASSED' ? unique : [],
            ...(vehicleClass !== undefined ? { vehicleClass: vehicleClass } : {}),
            plate: normalizePlate(plate),
            reasonCode: reasonCode,
            ...(note !== undefined ? { note } : {}),
            ...(validUntil !== undefined ? { validUntil } : {}),
        },
    };
}
/** Mask a document number, revealing at most the last 4 characters (the full value is never stored). */
export function maskDocumentNumber(raw) {
    const v = raw.replace(/\s+/g, '');
    if (v.length <= 4)
        return '*'.repeat(Math.max(1, v.length));
    return '*'.repeat(v.length - 4) + v.slice(-4);
}
//# sourceMappingURL=review.js.map